/**
 * Faces for modular figures, assembled from parts the way a character creator does: eyes, brows,
 * nose, mouth, ears, facial hair, earrings and marks (freckles, moles, scars…), each a small pixel
 * grid placed on the head's grid (see heads.ts). Eyes, brows and the mouth change with the
 * pose's mood — a shout when attacking, gritted teeth in a guard, closed eyes in meditation.
 *
 * Eyes and brows are drawn once, as the eye on the right of a face seen head-on (inner corner on
 * the left), and mirrored or narrowed for the other eye and the turned views. Other parts give a
 * stamp per view. Back views show only what sticks out past the head: earrings.
 *
 * Grid legend: `k` lash and lid, `w` eye white, `i` iris, `P` pupil, `I` shine, `b` brow, `n` nose and creases,
 * `m` lips, `L` painted lips, `M` open mouth, `t` teeth, `h` facial hair, `e` ear, `f` freckle, `r` spot, `B` blush,
 * `o` mole, `x` scar, `p` forehead mark, `g` gold, `G` silver, `j` jade, `W` white (bandage, pearl,
 * bone), `s` skin, `H` facial hair in shade, `u` tattoo ink, `c` bruise, `y` dirt.
 */
import type { View } from '../sprites';
import { FINE_PARTS, isFinePart } from './face2';

/** A grid whose top-left cell sits at (`x`, `y`) from the head joint. */
export interface Stamp {
  x: number;
  y: number;
  rows: readonly string[];
}

export type Mood = 'idle' | 'atk' | 'block' | 'cast' | 'closed';

export interface FacePart {
  id: string;
  label: string;
  /** Per-view stamps for parts that are not eyes or brows. */
  views?: Partial<Record<View, readonly Stamp[]>>;
  /** The right-hand eye or brow at rest, and its moods (fall back to `idle`). */
  grids?: Partial<Record<Mood, readonly string[]>>;
}

/** Rows below the head joint: brows, eyes, nose, mouth (as heads.ts places them), chin. */
/** The part id that picks the traced feature for a slot (drawn by the face layer at full resolution). */
export const REF_PART = 'ref';

export const ROW = { brow: -1, eye: 2, nose: 5, mouth: 7, chin: 9 } as const;

// ── eyes ──

const CLOSED = ['...', 'kkk'];
/** Eyes: the traced eyes and the variants made from them (face2.ts), at full resolution. */
const EYES: readonly FacePart[] = FINE_PARTS.eyes;

// ── brows ──

/** Brows: the traced brows and their variants (face2.ts), at full resolution. */
const BROWS: readonly FacePart[] = FINE_PARTS.brows;

// ── nose ──

/** Noses: the traced nose and its variants (face2.ts), at full resolution. */
const NOSES: readonly FacePart[] = FINE_PARTS.nose;

// ── mouth (the idle shape; moods override it) ──

/** Mouths: the traced mouth and its variants (face2.ts), at full resolution. */
const MOUTHS: readonly FacePart[] = FINE_PARTS.mouth;
/** Expressions every mouth shares: a shout on the attack, gritted teeth on guard or casting. */
const MOUTH_MOODS: Partial<Record<Mood, readonly string[]>> = {
  atk: ['mMMm', 'MttM', '.MM.'],
  block: ['mmmm', 'tttt'],
  cast: ['mmm'],
};

// ── ears (drawn over the head template's ears) ──

/**
 * Ears: the head grid's own (heads.ts); REF_PART is the only choice. The small stamped ones were
 * retired.
 */
const EARS: readonly FacePart[] = [{ id: REF_PART, label: 'ต้นแบบ' }];

// ── facial hair ──

const MUSTACHES: readonly FacePart[] = [
  { id: 'none', label: 'ไม่มี' },
  {
    id: 'thin',
    label: 'บาง',
    views: {
      front: [{ x: -2, y: ROW.mouth - 1, rows: ['hhhh'] }],
      front3: [{ x: 1, y: ROW.mouth - 1, rows: ['hhhh'] }],
      side: [{ x: 5, y: ROW.mouth - 1, rows: ['hh'] }],
    },
  },
  {
    id: 'thick',
    label: 'หนา',
    views: {
      front: [{ x: -3, y: ROW.mouth - 1, rows: ['.hhhh.', 'h....h'] }],
      front3: [{ x: 0, y: ROW.mouth - 1, rows: ['.hhhh.', 'h....h'] }],
      side: [{ x: 4, y: ROW.mouth - 1, rows: ['hhh', 'h..'] }],
    },
  },
  {
    id: 'whiskers',
    label: 'หนวดยาวห้อย',
    views: {
      front: [{ x: -3, y: ROW.mouth - 1, rows: ['.hhhh.', 'h....h', 'h....h', 'h....h', 'h....h'] }],
      front3: [{ x: 0, y: ROW.mouth - 1, rows: ['.hhhh.', 'h....h', 'h....h', 'h....h', 'h....h'] }],
      side: [{ x: 5, y: ROW.mouth - 1, rows: ['hh', 'h.', 'h.', 'h.', 'h.'] }],
    },
  },
  {
    id: 'handlebar',
    label: 'หนวดเขาควาย',
    views: {
      front: [{ x: -3, y: 5, rows: ['h....h', '.hhhh.'] }],
      front3: [{ x: 0, y: 5, rows: ['h....h', '.hhhh.'] }],
      side: [{ x: 4, y: 5, rows: ['..h', 'hh.'] }],
    },
  },
  {
    id: 'split',
    label: 'หนวดแยก',
    views: {
      front: [{ x: -3, y: 6, rows: ['hh..hh'] }],
      front3: [{ x: 0, y: 6, rows: ['hh..hh'] }],
      side: [{ x: 4, y: 6, rows: ['.hh'] }],
    },
  },
  {
    id: 'walrus',
    label: 'หนวดหนาปิดปาก',
    views: {
      front: [{ x: -3, y: 6, rows: ['hhhhhh', 'hhhhhh'] }],
      front3: [{ x: 0, y: 6, rows: ['hhhhhh', 'hhhhhh'] }],
      side: [{ x: 4, y: 6, rows: ['hhh', 'hhh'] }],
    },
  },
  {
    id: 'droopy',
    label: 'หนวดตก',
    views: {
      front: [{ x: -3, y: 6, rows: ['.hhhh.', 'h....h', 'h....h'] }],
      front3: [{ x: 0, y: 6, rows: ['.hhhh.', 'h....h', 'h....h'] }],
      side: [{ x: 4, y: 6, rows: ['hh.', '..h', '..h'] }],
    },
  },
  {
    id: 'twirl',
    label: 'หนวดม้วนปลาย',
    views: {
      front: [{ x: -4, y: 5, rows: ['h......h', '.h....h.', '..hhhh..'] }],
      front3: [{ x: -1, y: 5, rows: ['h......h', '.h....h.', '..hhhh..'] }],
      side: [{ x: 3, y: 5, rows: ['...h', '..h.', 'hh..'] }],
    },
  },
  {
    id: 'chevron',
    label: 'หนวดตัววี',
    views: {
      front: [{ x: -2, y: 6, rows: ['hhhh', 'h..h'] }],
      front3: [{ x: 1, y: 6, rows: ['hhhh', 'h..h'] }],
      side: [{ x: 5, y: 6, rows: ['hh', '.h'] }],
    },
  },
  {
    id: 'patch',
    label: 'หนวดจุด',
    views: {
      front: [{ x: -1, y: 6, rows: ['hh'] }],
      front3: [{ x: 2, y: 6, rows: ['hh'] }],
      side: [{ x: 6, y: 6, rows: ['h'] }],
    },
  },
  {
    id: 'stubble',
    label: 'หนวดเขียวครึ้ม',
    views: {
      front: [{ x: -3, y: 6, rows: ['h.h.h.'] }],
      front3: [{ x: 0, y: 6, rows: ['h.h.h.'] }],
      side: [{ x: 4, y: 6, rows: ['.h.'] }],
    },
  },
  // Long thin whiskers hanging like a dragon.
  {
    id: 'dragon',
    label: 'หนวดมังกร',
    views: {
      front: [{ x: -3, y: 6, rows: ['.hhhh.', 'h....h', 'h....h', 'h....h', 'h....h', 'h....h', '.h..h.'] }],
      front3: [{ x: 0, y: 6, rows: ['.hhhh.', 'h....h', 'h....h', 'h....h', 'h....h', 'h....h', '.h..h.'] }],
      side: [{ x: 4, y: 6, rows: ['hh.', '..h', '..h', '..h', '..h', '..h', '.h.'] }],
    },
  },
  {
    id: 'brush',
    label: 'หนวดแปรง',
    views: {
      front: [{ x: -2, y: 6, rows: ['hhhh', 'hhhh'] }],
      front3: [{ x: 1, y: 6, rows: ['hhhh', 'hhhh'] }],
      side: [{ x: 5, y: 6, rows: ['hh', 'hh'] }],
    },
  },
];

const BEARDS: readonly FacePart[] = [
  { id: 'none', label: 'ไม่มี' },
  {
    id: 'goatee',
    label: 'เคราแพะ',
    views: {
      front: [{ x: -1, y: ROW.chin - 1, rows: ['hh', 'hh', 'h.'] }],
      front3: [{ x: 2, y: ROW.chin - 1, rows: ['hh', 'hh', 'h.'] }],
      side: [{ x: 5, y: ROW.chin - 1, rows: ['hh', 'hh', '.h'] }],
    },
  },
  {
    id: 'full',
    label: 'เคราเต็มหน้า',
    views: {
      front: [
        {
          x: -7,
          y: ROW.nose,
          rows: [
            'h............h',
            'hh..........hh',
            '.hhh......hhh.',
            '..hhhhhhhhhh..',
            '...hhhhhhhh...',
            '....hhhhhh....',
            '.....hhhh.....',
          ],
        },
      ],
      front3: [
        {
          x: -6,
          y: ROW.nose,
          rows: [
            'h...........h',
            'hh.........hh',
            '.hhh......hh.',
            '..hhhhhhhhhh.',
            '....hhhhhhh..',
            '......hhhhh..',
            '........hhh..',
          ],
        },
      ],
      side: [
        {
          x: -1,
          y: ROW.nose,
          rows: ['hh.......', '.hhh.....', '..hhh.hh.', '...hhhhhh', '....hhhh.', '......hhh', '.......hh'],
        },
      ],
    },
  },
  {
    id: 'long',
    label: 'เครายาวผู้อาวุโส',
    views: {
      front: [
        { x: -2, y: ROW.chin - 1, rows: ['hhhh', 'hhhh', 'hhhh', '.hh.', '.hh.', '.hh.', '.h..', '..h.'] },
      ],
      front3: [
        { x: 1, y: ROW.chin - 1, rows: ['hhhh', 'hhhh', 'hhh.', '.hh.', '.hh.', '.h..', '.h..', 'h...'] },
      ],
      side: [
        { x: 3, y: ROW.chin - 1, rows: ['hhhh', '.hhh', '.hhh', '.hh.', 'hh..', 'h...', 'h...', 'h...'] },
      ],
    },
  },
  {
    id: 'stubble',
    label: 'เคราครึ้ม',
    views: {
      front: [
        { x: -5, y: 7, rows: ['h.h.h.h.h.h'] },
        { x: -4, y: 8, rows: ['.h.h.h.h.'] },
      ],
      front3: [{ x: -2, y: 7, rows: ['h.h.h.h.h'] }],
      side: [{ x: 2, y: 7, rows: ['h.h.h'] }],
    },
  },
  // A thin line of beard along the jaw.
  {
    id: 'chinstrap',
    label: 'เคราไรกราม',
    views: {
      front: [
        { x: -7, y: 5, rows: ['h............h', '.h..........h.', '..h........h..', '...hhhhhhhh...'] },
      ],
      front3: [{ x: -5, y: 5, rows: ['h..........h', '.h........h.', '..hhhhhhhh..'] }],
      side: [{ x: -1, y: 5, rows: ['h......', '.h.....', '..h....', '...hhhh'] }],
    },
  },
  {
    id: 'forked',
    label: 'เคราสองแฉก',
    views: {
      front: [{ x: -2, y: 8, rows: ['hhhh', 'hhhh', 'h..h', 'h..h'] }],
      front3: [{ x: 1, y: 8, rows: ['hhhh', 'hhhh', 'h..h', 'h..h'] }],
      side: [{ x: 5, y: 8, rows: ['hh', 'hh', '.h', '.h'] }],
    },
  },
  {
    id: 'braided',
    label: 'เคราถักเปีย',
    views: {
      front: [{ x: -1, y: 8, rows: ['hh', 'hh', 'hH', 'Hh', 'hH', 'Hh', 'h.'] }],
      front3: [{ x: 2, y: 8, rows: ['hh', 'hh', 'hH', 'Hh', 'hH', 'Hh', 'h.'] }],
      side: [{ x: 6, y: 8, rows: ['h', 'h', 'H', 'h', 'H', 'h', '.'] }],
    },
  },
  {
    id: 'shortfull',
    label: 'เคราเต็มสั้น',
    views: {
      front: [{ x: -6, y: 5, rows: ['h..........h', 'hh........hh', '.hhhhhhhhhh.', '..hhhhhhhh..'] }],
      front3: [{ x: -4, y: 5, rows: ['h.........h', 'hh.......hh', '.hhhhhhhhh.', '..hhhhhhh..'] }],
      side: [{ x: -1, y: 5, rows: ['hh.....', '.hhh...', '..hhhhh', '...hhh.'] }],
    },
  },
  // Long enough to tuck into the belt.
  {
    id: 'wizard',
    label: 'เคราผู้วิเศษ',
    views: {
      front: [
        {
          x: -3,
          y: 8,
          rows: ['hhhhhh', 'hhhhhh', '.hhhh.', '.hhhh.', '..hh..', '..hh..', '..hh..', '..h...', '..h...'],
        },
      ],
      front3: [
        {
          x: 0,
          y: 8,
          rows: ['hhhhhh', 'hhhhhh', '.hhhh.', '.hhhh.', '..hh..', '..hh..', '..hh..', '..h...', '..h...'],
        },
      ],
      side: [{ x: 4, y: 8, rows: ['hhh', 'hhh', 'hh.', 'hh.', 'h..', 'h..', 'h..', '...', '...'] }],
    },
  },
  {
    id: 'spiky',
    label: 'เคราแหลม',
    views: {
      front: [{ x: -3, y: 8, rows: ['hhhhhh', 'h.hh.h', '..h...'] }],
      front3: [{ x: 0, y: 8, rows: ['hhhhhh', 'h.hh.h', '..h...'] }],
      side: [{ x: 4, y: 8, rows: ['hhh', 'h.h', '...'] }],
    },
  },
  {
    id: 'soulpatch',
    label: 'เคราจุดใต้ปาก',
    views: {
      front: [{ x: -1, y: 8, rows: ['hh'] }],
      front3: [{ x: 2, y: 8, rows: ['hh'] }],
      side: [{ x: 6, y: 8, rows: ['h'] }],
    },
  },
  {
    id: 'longgoat',
    label: 'เคราแพะยาว',
    views: {
      front: [{ x: -1, y: 8, rows: ['hh', 'hh', 'hh', 'h.', 'h.', '.h'] }],
      front3: [{ x: 2, y: 8, rows: ['hh', 'hh', 'hh', 'h.', 'h.', '.h'] }],
      side: [{ x: 6, y: 8, rows: ['h', 'h', 'h', '.', '.', 'h'] }],
    },
  },
  {
    id: 'square',
    label: 'เคราเหลี่ยม',
    views: {
      front: [{ x: -4, y: 7, rows: ['hhhhhhhh', 'hhhhhhhh', 'hhhhhhhh'] }],
      front3: [{ x: -1, y: 7, rows: ['hhhhhhhh', 'hhhhhhhh', 'hhhhhhhh'] }],
      side: [{ x: 3, y: 7, rows: ['hhhh', 'hhhh', 'hhhh'] }],
    },
  },
];

const SIDEBURNS: readonly FacePart[] = [
  { id: 'none', label: 'ไม่มี' },
  {
    id: 'short',
    label: 'สั้น',
    views: {
      front: [
        { x: -8, y: 0, rows: ['h', 'h', 'h', 'h'] },
        { x: 7, y: 0, rows: ['h', 'h', 'h', 'h'] },
      ],
      front3: [{ x: -4, y: 0, rows: ['h', 'h', 'h', 'h'] }],
      side: [{ x: -1, y: 0, rows: ['hh', 'hh', 'hh', '.h'] }],
    },
  },
  {
    id: 'long',
    label: 'ยาวถึงกราม',
    views: {
      front: [
        { x: -8, y: 0, rows: ['h.', 'h.', 'h.', 'h.', 'hh', 'hh', '.h'] },
        { x: 6, y: 0, rows: ['.h', '.h', '.h', '.h', 'hh', 'hh', 'h.'] },
      ],
      front3: [{ x: -4, y: 0, rows: ['h.', 'h.', 'h.', 'hh', 'hh', '.h', '.h'] }],
      side: [{ x: -1, y: 0, rows: ['hh.', 'hh.', 'hh.', 'hhh', '.hh', '..h', '..h'] }],
    },
  },
  {
    id: 'thin',
    label: 'จอนเส้นบาง',
    views: {
      front: [
        { x: -8, y: 0, rows: ['h', 'h', 'h', 'h', 'h', 'h'] },
        { x: 7, y: 0, rows: ['h', 'h', 'h', 'h', 'h', 'h'] },
      ],
      front3: [{ x: -4, y: 0, rows: ['h', 'h', 'h', 'h', 'h', 'h'] }],
      side: [{ x: -1, y: 0, rows: ['h', 'h', 'h', 'h', 'h', 'h'] }],
    },
  },
  {
    id: 'wide',
    label: 'จอนกว้าง',
    views: {
      front: [
        { x: -8, y: 0, rows: ['hh', 'hh', 'hh', 'hh', 'hh', 'hh'] },
        { x: 6, y: 0, rows: ['hh', 'hh', 'hh', 'hh', 'hh', 'hh'] },
      ],
      front3: [{ x: -4, y: 0, rows: ['hh', 'hh', 'hh', 'hh', 'hh', 'hh'] }],
      side: [{ x: -1, y: 0, rows: ['hh', 'hh', 'hh', 'hh', 'hh', 'hh'] }],
    },
  },
  // Mutton chops spreading over the cheeks.
  {
    id: 'mutton',
    label: 'จอนแก้มกว้าง',
    views: {
      front: [
        { x: -8, y: 0, rows: ['h..', 'h..', 'hh.', 'hh.', 'hhh', 'hhh'] },
        { x: 5, y: 0, rows: ['..h', '..h', '.hh', '.hh', 'hhh', 'hhh'] },
      ],
      front3: [{ x: -5, y: 0, rows: ['h..', 'h..', 'hh.', 'hh.', 'hhh', 'hhh'] }],
      side: [{ x: -2, y: 0, rows: ['h..', 'h..', 'hh.', 'hh.', 'hhh', 'hhh'] }],
    },
  },
  {
    id: 'pointed',
    label: 'จอนแหลม',
    views: {
      front: [
        { x: -8, y: 0, rows: ['hh', 'hh', 'hh', 'h.', 'h.'] },
        { x: 6, y: 0, rows: ['hh', 'hh', 'hh', '.h', '.h'] },
      ],
      front3: [{ x: -4, y: 0, rows: ['hh', 'hh', 'hh', 'h.', 'h.'] }],
      side: [{ x: -1, y: 0, rows: ['hh', 'hh', 'hh', 'h.', 'h.'] }],
    },
  },
  {
    id: 'curly',
    label: 'จอนหยิก',
    views: {
      front: [
        { x: -8, y: 0, rows: ['h.', '.h', 'h.', '.h', 'h.'] },
        { x: 6, y: 0, rows: ['.h', 'h.', '.h', 'h.', '.h'] },
      ],
      front3: [{ x: -4, y: 0, rows: ['h.', '.h', 'h.', '.h', 'h.'] }],
      side: [{ x: -1, y: 0, rows: ['h.', '.h', 'h.', '.h', 'h.'] }],
    },
  },
  // A thin braid hanging from each temple.
  {
    id: 'braid',
    label: 'จอนถักเปีย',
    views: {
      front: [
        { x: -8, y: 0, rows: ['h', 'H', 'h', 'H', 'h', 'H', 'h', 'H', 'h', 'H', 'h'] },
        { x: 7, y: 0, rows: ['h', 'H', 'h', 'H', 'h', 'H', 'h', 'H', 'h', 'H', 'h'] },
      ],
      front3: [{ x: -4, y: 0, rows: ['h', 'H', 'h', 'H', 'h', 'H', 'h', 'H', 'h', 'H', 'h'] }],
      side: [{ x: -1, y: 0, rows: ['h', 'H', 'h', 'H', 'h', 'H', 'h', 'H', 'h', 'H', 'h'] }],
    },
  },
  {
    id: 'square',
    label: 'จอนเหลี่ยม',
    views: {
      front: [
        { x: -8, y: 1, rows: ['hh', 'hh', 'hh', 'hh'] },
        { x: 6, y: 1, rows: ['hh', 'hh', 'hh', 'hh'] },
      ],
      front3: [{ x: -4, y: 1, rows: ['hh', 'hh', 'hh', 'hh'] }],
      side: [{ x: -1, y: 1, rows: ['hh', 'hh', 'hh', 'hh'] }],
    },
  },
  {
    id: 'connected',
    label: 'จอนต่อเครา',
    views: {
      front: [
        { x: -8, y: 0, rows: ['h.', 'h.', 'h.', 'hh', '.h', '.hh', '..h'] },
        { x: 6, y: 0, rows: ['.h', '.h', '.h', 'hh', 'h.', 'hh.', 'h..'] },
      ],
      front3: [{ x: -4, y: 0, rows: ['h.', 'h.', 'h.', 'hh', '.h', '.hh', '..h'] }],
      side: [{ x: -1, y: 0, rows: ['h.', 'h.', 'h.', 'hh', '.h', '.hh', '..h'] }],
    },
  },
  {
    id: 'thick',
    label: 'จอนหนาสั้น',
    views: {
      front: [
        { x: -8, y: 3, rows: ['hhh', 'hhh', 'hhh'] },
        { x: 5, y: 3, rows: ['hhh', 'hhh', 'hhh'] },
      ],
      front3: [{ x: -5, y: 3, rows: ['hhh', 'hhh', 'hhh'] }],
      side: [{ x: -2, y: 3, rows: ['hhh', 'hhh', 'hhh'] }],
    },
  },
  {
    id: 'flared',
    label: 'จอนบานปลาย',
    views: {
      front: [
        { x: -8, y: 1, rows: ['h..', 'h..', 'hh.', 'hhh'] },
        { x: 5, y: 1, rows: ['..h', '..h', '.hh', 'hhh'] },
      ],
      front3: [{ x: -5, y: 1, rows: ['h..', 'h..', 'hh.', 'hhh'] }],
      side: [{ x: -2, y: 1, rows: ['h..', 'h..', 'hh.', 'hhh'] }],
    },
  },
];

// ── earrings ──

const EARRINGS: readonly FacePart[] = [
  { id: 'none', label: 'ไม่มี' },
  {
    id: 'drop',
    label: 'ต่างหูทอง',
    views: {
      front: [
        { x: -9, y: 3, rows: ['g', 'g'] },
        { x: 8, y: 3, rows: ['g', 'g'] },
      ],
      front3: [{ x: -6, y: 3, rows: ['g', 'g'] }],
      side: [{ x: -3, y: 3, rows: ['g', 'g'] }],
      back3: [{ x: 5, y: 3, rows: ['g', 'g'] }],
      back: [
        { x: -9, y: 3, rows: ['g', 'g'] },
        { x: 8, y: 3, rows: ['g', 'g'] },
      ],
    },
  },
  {
    id: 'hoop',
    label: 'ต่างหูห่วงทอง',
    views: {
      front: [
        { x: -10, y: 3, rows: ['.g.', 'g.g', '.g.'] },
        { x: 7, y: 3, rows: ['.g.', 'g.g', '.g.'] },
      ],
      front3: [{ x: -7, y: 3, rows: ['.g.', 'g.g', '.g.'] }],
      side: [{ x: -4, y: 3, rows: ['.g.', 'g.g', '.g.'] }],
      back3: [{ x: 4, y: 3, rows: ['.g.', 'g.g', '.g.'] }],
      back: [
        { x: -10, y: 3, rows: ['.g.', 'g.g', '.g.'] },
        { x: 7, y: 3, rows: ['.g.', 'g.g', '.g.'] },
      ],
    },
  },
  {
    id: 'pearl',
    label: 'ต่างหูไข่มุก',
    views: {
      front: [
        { x: -9, y: 3, rows: ['g', 'W'] },
        { x: 8, y: 3, rows: ['g', 'W'] },
      ],
      front3: [{ x: -6, y: 3, rows: ['g', 'W'] }],
      side: [{ x: -3, y: 3, rows: ['g', 'W'] }],
      back3: [{ x: 5, y: 3, rows: ['g', 'W'] }],
      back: [
        { x: -9, y: 3, rows: ['g', 'W'] },
        { x: 8, y: 3, rows: ['g', 'W'] },
      ],
    },
  },
  {
    id: 'jade',
    label: 'ต่างหูหยก',
    views: {
      front: [
        { x: -9, y: 3, rows: ['g', 'j', 'j'] },
        { x: 8, y: 3, rows: ['g', 'j', 'j'] },
      ],
      front3: [{ x: -6, y: 3, rows: ['g', 'j', 'j'] }],
      side: [{ x: -3, y: 3, rows: ['g', 'j', 'j'] }],
      back3: [{ x: 5, y: 3, rows: ['g', 'j', 'j'] }],
      back: [
        { x: -9, y: 3, rows: ['g', 'j', 'j'] },
        { x: 8, y: 3, rows: ['g', 'j', 'j'] },
      ],
    },
  },
  {
    id: 'tassel',
    label: 'ต่างหูพู่แดง',
    views: {
      front: [
        { x: -9, y: 3, rows: ['g', 'r', 'r', 'r'] },
        { x: 8, y: 3, rows: ['g', 'r', 'r', 'r'] },
      ],
      front3: [{ x: -6, y: 3, rows: ['g', 'r', 'r', 'r'] }],
      side: [{ x: -3, y: 3, rows: ['g', 'r', 'r', 'r'] }],
      back3: [{ x: 5, y: 3, rows: ['g', 'r', 'r', 'r'] }],
      back: [
        { x: -9, y: 3, rows: ['g', 'r', 'r', 'r'] },
        { x: 8, y: 3, rows: ['g', 'r', 'r', 'r'] },
      ],
    },
  },
  {
    id: 'chain',
    label: 'ต่างหูโซ่ห้อย',
    views: {
      front: [
        { x: -9, y: 3, rows: ['g', 'g', 'g', 'j'] },
        { x: 8, y: 3, rows: ['g', 'g', 'g', 'j'] },
      ],
      front3: [{ x: -6, y: 3, rows: ['g', 'g', 'g', 'j'] }],
      side: [{ x: -3, y: 3, rows: ['g', 'g', 'g', 'j'] }],
      back3: [{ x: 5, y: 3, rows: ['g', 'g', 'g', 'j'] }],
      back: [
        { x: -9, y: 3, rows: ['g', 'g', 'g', 'j'] },
        { x: 8, y: 3, rows: ['g', 'g', 'g', 'j'] },
      ],
    },
  },
  {
    id: 'studs',
    label: 'ต่างหูเม็ดคู่',
    views: {
      front: [
        { x: -9, y: 2, rows: ['g', '.', 'g'] },
        { x: 8, y: 2, rows: ['g', '.', 'g'] },
      ],
      front3: [{ x: -6, y: 2, rows: ['g', '.', 'g'] }],
      side: [{ x: -3, y: 2, rows: ['g', '.', 'g'] }],
      back3: [{ x: 5, y: 2, rows: ['g', '.', 'g'] }],
      back: [
        { x: -9, y: 2, rows: ['g', '.', 'g'] },
        { x: 8, y: 2, rows: ['g', '.', 'g'] },
      ],
    },
  },
  {
    id: 'feather',
    label: 'ต่างหูขนนก',
    views: {
      front: [
        { x: -9, y: 3, rows: ['g.', 'h.', 'hh', '.h'] },
        { x: 7, y: 3, rows: ['.g', '.h', 'hh', 'h.'] },
      ],
      front3: [{ x: -6, y: 3, rows: ['g.', 'h.', 'hh', '.h'] }],
      side: [{ x: -3, y: 3, rows: ['g.', 'h.', 'hh', '.h'] }],
      back3: [{ x: 4, y: 3, rows: ['.g', '.h', 'hh', 'h.'] }],
      back: [
        { x: -9, y: 3, rows: ['g.', 'h.', 'hh', '.h'] },
        { x: 7, y: 3, rows: ['.g', '.h', 'hh', 'h.'] },
      ],
    },
  },
  {
    id: 'crescent',
    label: 'ต่างหูจันทร์เสี้ยว',
    views: {
      front: [
        { x: -10, y: 3, rows: ['g.', '.g', 'g.'] },
        { x: 8, y: 3, rows: ['.g', 'g.', '.g'] },
      ],
      front3: [{ x: -7, y: 3, rows: ['g.', '.g', 'g.'] }],
      side: [{ x: -4, y: 3, rows: ['g.', '.g', 'g.'] }],
      back3: [{ x: 5, y: 3, rows: ['.g', 'g.', '.g'] }],
      back: [
        { x: -10, y: 3, rows: ['g.', '.g', 'g.'] },
        { x: 8, y: 3, rows: ['.g', 'g.', '.g'] },
      ],
    },
  },
  {
    id: 'bone',
    label: 'ต่างหูกระดูก',
    views: {
      front: [
        { x: -9, y: 3, rows: ['W', 'W', 'W'] },
        { x: 8, y: 3, rows: ['W', 'W', 'W'] },
      ],
      front3: [{ x: -6, y: 3, rows: ['W', 'W', 'W'] }],
      side: [{ x: -3, y: 3, rows: ['W', 'W', 'W'] }],
      back3: [{ x: 5, y: 3, rows: ['W', 'W', 'W'] }],
      back: [
        { x: -9, y: 3, rows: ['W', 'W', 'W'] },
        { x: 8, y: 3, rows: ['W', 'W', 'W'] },
      ],
    },
  },
  {
    id: 'silver',
    label: 'ต่างหูห่วงเงิน',
    views: {
      front: [
        { x: -10, y: 3, rows: ['.G.', 'G.G', '.G.'] },
        { x: 7, y: 3, rows: ['.G.', 'G.G', '.G.'] },
      ],
      front3: [{ x: -7, y: 3, rows: ['.G.', 'G.G', '.G.'] }],
      side: [{ x: -4, y: 3, rows: ['.G.', 'G.G', '.G.'] }],
      back3: [{ x: 4, y: 3, rows: ['.G.', 'G.G', '.G.'] }],
      back: [
        { x: -10, y: 3, rows: ['.G.', 'G.G', '.G.'] },
        { x: 7, y: 3, rows: ['.G.', 'G.G', '.G.'] },
      ],
    },
  },
];

// ── marks (several can be worn) ──

const MARKS: readonly FacePart[] = [
  {
    id: 'freckles',
    label: 'กระ',
    views: {
      front: [
        { x: -6, y: ROW.nose, rows: ['f.f', '.f.'] },
        { x: 3, y: ROW.nose, rows: ['f.f', '.f.'] },
      ],
      front3: [
        { x: -3, y: ROW.nose, rows: ['f.', '.f'] },
        { x: 4, y: ROW.nose, rows: ['f.f', '.f.'] },
      ],
      side: [{ x: 2, y: ROW.nose, rows: ['f.f', '.f.'] }],
    },
  },
  {
    id: 'acne',
    label: 'สิว',
    views: {
      front: [
        { x: -6, y: ROW.nose + 1, rows: ['r'] },
        { x: 4, y: ROW.nose, rows: ['r.', '.r'] },
        { x: 1, y: -2, rows: ['r'] },
      ],
      front3: [
        { x: 5, y: ROW.nose, rows: ['r.', '.r'] },
        { x: 3, y: -2, rows: ['r'] },
      ],
      side: [{ x: 3, y: ROW.nose, rows: ['r.', '.r'] }],
    },
  },
  {
    id: 'mole',
    label: 'ไฝ',
    views: {
      front: [{ x: 2, y: ROW.mouth, rows: ['o'] }],
      front3: [{ x: 5, y: ROW.mouth, rows: ['o'] }],
      side: [{ x: 4, y: ROW.mouth, rows: ['o'] }],
    },
  },
  {
    id: 'blush',
    label: 'แก้มแดง',
    views: {
      front: [
        { x: -7, y: ROW.nose, rows: ['BB'] },
        { x: 4, y: ROW.nose, rows: ['BB'] },
      ],
      front3: [
        { x: -3, y: ROW.nose, rows: ['B'] },
        { x: 5, y: ROW.nose, rows: ['BB'] },
      ],
      side: [{ x: 3, y: ROW.nose, rows: ['BB'] }],
    },
  },
  {
    id: 'scar',
    label: 'แผลเป็น',
    views: {
      front: [{ x: 2, y: ROW.brow - 1, rows: ['x..', 'x..', '...', '...', '...', '.x.', '..x'] }],
      front3: [{ x: 4, y: ROW.brow - 1, rows: ['x..', 'x..', '...', '...', '...', '.x.', '..x'] }],
      side: [{ x: 3, y: ROW.brow - 1, rows: ['x..', 'x..', '...', '...', '...', '.x.', '..x'] }],
    },
  },
  {
    id: 'mark',
    label: 'แต้มหน้าผาก',
    views: {
      front: [{ x: -2, y: -4, rows: ['.p.', 'p.p', '.p.'] }],
      front3: [{ x: 1, y: -4, rows: ['.p.', 'p.p', '.p.'] }],
      side: [{ x: 6, y: -4, rows: ['p', 'p'] }],
    },
  },
  {
    id: 'wrinkles',
    label: 'ริ้วรอย',
    views: {
      front: [
        { x: -3, y: -3, rows: ['nnnnnn', '......', '.nnnn.'] },
        { x: -4, y: ROW.mouth - 1, rows: ['n......n', 'n......n'] },
        { x: -6, y: ROW.eye + 3, rows: ['.n.......n.'] },
      ],
      front3: [
        { x: 0, y: -3, rows: ['nnnnn', '.....', '.nnn.'] },
        { x: 0, y: ROW.mouth - 1, rows: ['n.....', 'n.....'] },
        { x: 4, y: ROW.eye + 3, rows: ['.nn'] },
      ],
      side: [
        { x: 2, y: -3, rows: ['nnnn', '....', '.nnn'] },
        { x: 3, y: ROW.mouth - 1, rows: ['n', 'n'] },
      ],
    },
  },
  // A beauty mark just under the eye.
  {
    id: 'tearmole',
    label: 'ไฝใต้ตา',
    views: {
      front: [{ x: 3, y: 4, rows: ['o'] }],
      front3: [{ x: 5, y: 4, rows: ['o'] }],
      side: [{ x: 4, y: 4, rows: ['o'] }],
    },
  },
  {
    id: 'crossscar',
    label: 'แผลกากบาท',
    views: {
      front: [{ x: -6, y: 4, rows: ['x.x', '.x.', 'x.x'] }],
      front3: [{ x: -3, y: 4, rows: ['x.x', '.x.', 'x.x'] }],
      side: [{ x: 2, y: 4, rows: ['x.x', '.x.', 'x.x'] }],
    },
  },
  // A tattooed sigil on the brow.
  {
    id: 'rune',
    label: 'รอยสักอักขระ',
    views: {
      front: [{ x: -2, y: -4, rows: ['.u.', 'uuu', 'u.u'] }],
      front3: [{ x: 1, y: -4, rows: ['.u.', 'uuu', 'u.u'] }],
      side: [{ x: 6, y: -4, rows: ['u', 'u', 'u'] }],
    },
  },
  // Stripes of war paint across the cheeks.
  {
    id: 'warpaint',
    label: 'สีศึก',
    views: {
      front: [
        { x: -7, y: 4, rows: ['pp'] },
        { x: 4, y: 4, rows: ['pp'] },
        { x: -7, y: 6, rows: ['pp'] },
        { x: 4, y: 6, rows: ['pp'] },
      ],
      front3: [
        { x: -3, y: 4, rows: ['pp'] },
        { x: 5, y: 4, rows: ['pp'] },
        { x: 5, y: 6, rows: ['pp'] },
      ],
      side: [
        { x: 3, y: 4, rows: ['pp'] },
        { x: 3, y: 6, rows: ['pp'] },
      ],
    },
  },
  // A vertical mark on the brow, the inner eye.
  {
    id: 'thirdeye',
    label: 'ตาที่สาม',
    views: {
      front: [{ x: -1, y: -4, rows: ['p', 'p', 'p'] }],
      front3: [{ x: 2, y: -4, rows: ['p', 'p', 'p'] }],
      side: [{ x: 6, y: -4, rows: ['p', 'p'] }],
    },
  },
  // A dragon tattooed curling down the cheek.
  {
    id: 'dragon',
    label: 'สักมังกรแก้ม',
    views: {
      front: [{ x: 3, y: 4, rows: ['u.u', '.u.', 'u..', '.uu'] }],
      front3: [{ x: 5, y: 4, rows: ['u.u', '.u.', 'u..', '.uu'] }],
      side: [{ x: 3, y: 4, rows: ['u.u', '.u.', 'u..'] }],
    },
  },
  {
    id: 'bruise',
    label: 'รอยช้ำ',
    views: {
      front: [{ x: 3, y: 4, rows: ['cc', 'cc'] }],
      front3: [{ x: 5, y: 4, rows: ['cc', 'cc'] }],
      side: [{ x: 4, y: 4, rows: ['cc', 'cc'] }],
    },
  },
  {
    id: 'dirt',
    label: 'คราบฝุ่น',
    views: {
      front: [
        { x: -6, y: 5, rows: ['y.y'] },
        { x: 3, y: 6, rows: ['yy'] },
      ],
      front3: [
        { x: -2, y: 5, rows: ['y.y'] },
        { x: 4, y: 6, rows: ['yy'] },
      ],
      side: [{ x: 3, y: 5, rows: ['y.y'] }],
    },
  },
  // A drinker's flushed cheeks.
  {
    id: 'flush',
    label: 'แก้มแดงเมา',
    views: {
      front: [
        { x: -7, y: 5, rows: ['BBB'] },
        { x: 4, y: 5, rows: ['BBB'] },
      ],
      front3: [
        { x: -3, y: 5, rows: ['BB'] },
        { x: 4, y: 5, rows: ['BBB'] },
      ],
      side: [{ x: 2, y: 5, rows: ['BBB'] }],
    },
  },
  // A scar running down through one eye.
  {
    id: 'eyescar',
    label: 'แผลผ่านตา',
    views: {
      front: [{ x: 3, y: 0, rows: ['x', 'x', '.', '.', '.', 'x', 'x'] }],
      front3: [{ x: 5, y: 0, rows: ['x', 'x', '.', '.', '.', 'x', 'x'] }],
      side: [{ x: 4, y: 0, rows: ['x', 'x', '.', '.', '.', 'x', 'x'] }],
    },
  },
];

export const FACE_PARTS = {
  eyes: EYES,
  brows: BROWS,
  nose: NOSES,
  mouth: MOUTHS,
  ears: EARS,
  mustache: MUSTACHES,
  beard: BEARDS,
  sideburns: SIDEBURNS,
  earrings: EARRINGS,
  marks: MARKS,
} as const;

export type FaceSlot = Exclude<keyof typeof FACE_PARTS, 'marks'>;

/** A face: one part per slot, any number of marks, and the colours that vary by character. */
export interface FaceLook {
  eyes: string;
  brows: string;
  nose: string;
  mouth: string;
  ears: string;
  mustache: string;
  beard: string;
  sideburns: string;
  earrings: string;
  marks: readonly string[];
  /** The coloured ring of the eye (ตาดำ); its pupil is a deeper shade of it. */
  iris: string;
  /** The white of the eye (ตาขาว). */
  sclera: string;
  /** Brows and facial hair; defaults to the hair colour. */
  facialHair?: string;
}

export const DEFAULT_FACE: FaceLook = {
  eyes: REF_PART,
  brows: REF_PART,
  nose: REF_PART,
  mouth: REF_PART,
  ears: REF_PART,
  mustache: 'none',
  beard: 'none',
  sideburns: 'none',
  earrings: 'none',
  marks: [],
  iris: '#3a2418',
  sclera: '#f6f2ec',
};

const part = (slot: keyof typeof FACE_PARTS, id: string) => FACE_PARTS[slot].find((p) => p.id === id);
const mirror = (rows: readonly string[]) => rows.map((r) => [...r].reverse().join(''));
const crop = (rows: readonly string[], from: number, to: number) => rows.map((r) => r.slice(from, to));

/** Where the right-hand eye and brow start in each view, and how wide the far one shows. */
const EYE_X: Partial<Record<View, number>> = { front: 2, front3: 4 };

/**
 * Every stamp of a face for a view and mood: eyes, brows and mouth placed from their single
 * drawings, then the other parts' own stamps. Back views keep only parts that show past the head.
 */
export function faceStamps(f: FaceLook, view: View, mood: Mood): { inner: Stamp[]; hair: Stamp[] } {
  const inner: Stamp[] = [];
  const hair: Stamp[] = [];
  const pair = (rows: readonly string[] | undefined, y: number) => {
    if (!rows?.length) return;
    const w = rows[0]!.length;
    if (view === 'front') {
      inner.push({ x: EYE_X.front!, y, rows });
      // The mirror image about the face's centre line, between columns -1 and 0.
      inner.push({ x: -1 - EYE_X.front! - (w - 1), y, rows: mirror(rows) });
    } else if (view === 'front3') {
      inner.push({ x: EYE_X.front3!, y, rows });
      // The far one, foreshortened: its outer column turned out of sight.
      inner.push({ x: -2, y, rows: crop(mirror(rows), 1, 3) });
    } else if (view === 'side') {
      inner.push({ x: 3, y, rows: crop(mirror(rows), Math.max(0, w - 2), w) });
    }
  };
  if (view !== 'back' && view !== 'back3') {
    // A full-resolution part (face2.ts) is drawn by the face layer instead.
    const eyes = part('eyes', f.eyes)?.grids;
    const eyeMood = mood === 'cast' ? 'block' : mood;
    if (!isFinePart('eyes', f.eyes))
      pair(eyes?.[eyeMood] ?? (mood === 'closed' ? CLOSED : eyes?.idle), ROW.eye);
    const brows = part('brows', f.brows)?.grids;
    const angry = mood === 'atk' || mood === 'block';
    if (!isFinePart('brows', f.brows)) pair(angry ? (brows?.atk ?? brows?.idle) : brows?.idle, ROW.brow);
    const moodMouth = mood !== 'idle' && mood !== 'closed' ? MOUTH_MOODS[mood] : undefined;
    const mouth =
      moodMouth ??
      (isFinePart('mouth', f.mouth) ? undefined : (part('mouth', f.mouth)?.grids?.idle ?? ['mm']));
    if (mouth && (f.beard !== 'long' || mood === 'atk')) {
      const w = mouth[0]!.length;
      const y = ROW.mouth - (mouth.length > 2 ? 1 : 0);
      if (view === 'front') inner.push({ x: -Math.floor(w / 2), y, rows: mouth });
      else if (view === 'front3') inner.push({ x: 3 - Math.floor(w / 2), y, rows: mouth });
      else inner.push({ x: 5, y, rows: crop(mouth, Math.floor(w / 2), w) });
    }
  }
  const add = (slot: keyof typeof FACE_PARTS, id: string, to: Stamp[]) =>
    to.push(...(part(slot, id)?.views?.[view] ?? []));
  add('nose', f.nose, inner);
  for (const m of f.marks) add('marks', m, inner);
  // Pointed ear tips stand up through the hair.
  add('ears', f.ears, hair);
  add('sideburns', f.sideburns, hair);
  add('beard', f.beard, hair);
  add('mustache', f.mustache, hair);
  // Seen from behind, earrings go under the hair: only what hangs past the head and hair shows.
  add('earrings', f.earrings, view === 'back' || view === 'back3' ? inner : hair);
  return { inner, hair };
}
